import { motion, useInView } from 'framer-motion';
import { type CSSProperties, forwardRef, useCallback, useEffect } from 'react';

import {
  Box,
  type BoxProps,
  Drawer,
  DrawerContent,
  VStack,
  useToken,
  rem,
  useBreakpointValue,
} from '@vimeo/bokeh';

import { NAV_HEIGHT } from '../../constants';
import { useLayout } from '../../hooks/use-layout';
import { ModuleWrapper } from '../ModuleWrapper';
import { ClipPageBase } from './components/ClipPageBase';
import {
  returnActiveGridTemplateName,
  createGridTemplateAreasConfig,
  createShowcaseGridTemplateAreasConfig,
  getAsideHeightStyles,
  getAsideTopStyles,
} from './utils';

/**
 * Page layout for the Clip page - all privacies
 * Handles the layout of the page and the drawer state changes
 */
export const ClipLayout = ({
  canSeePlaylist = false,
  showComments = false,
  showcaseConfig,
  isLiveEventView = false,
  ...rest
}: React.PropsWithChildren<{
  canSeePlaylist?: boolean;
  showComments?: boolean;
  showcaseConfig?: {
    isShowcaseClipView?: boolean;
    isShowcaseEmpty?: boolean;
    isShowcaseMobilePreview?: boolean;
  };
  isLiveEventView?: boolean;
}>) => {
  const { isSideBySideActive, isSideModuleOpen, isShowcaseLayout } = useLayout();
  const isShowcaseClipView = showcaseConfig?.isShowcaseClipView || false;

  // Define the grid template areas for the different possible layouts based on module visibility
  const gridTemplateAreasConfig = isShowcaseLayout
    ? createShowcaseGridTemplateAreasConfig(
        isShowcaseClipView,
        isLiveEventView || showComments,
        showComments,
      )
    : createGridTemplateAreasConfig(showComments, canSeePlaylist);

  // Return the active grid template name based on the current layout settings
  const activeGridTemplateName = returnActiveGridTemplateName(isSideModuleOpen, isSideBySideActive);

  // The active grid template areas that will be applied to the grid on mobile and desktop
  const gridTemplateAreas = useBreakpointValue(
    {
      base: gridTemplateAreasConfig.MOBILE,
      md: gridTemplateAreasConfig[activeGridTemplateName],
    },
    { ssr: true, fallback: 'md' },
  );

  return (
    <ClipPageBase
      data-vh-view="vh-player-view"
      isSideBySideActive={isSideBySideActive}
      gridTemplateAreas={gridTemplateAreas}
      showcaseConfig={showcaseConfig}
      {...rest}
    />
  );
};

const Media = forwardRef<HTMLDivElement, React.PropsWithChildren<BoxProps>>(
  ({ children, backgroundColor }, ref) => {
    return (
      <ModuleWrapper
        ref={ref}
        gridArea="media"
        w="100%"
        h="100%"
        zIndex={2}
        backgroundColor={backgroundColor}
        position="unset"
      >
        {children}
      </ModuleWrapper>
    );
  },
);

/**
 * Vertical stack on the Clip page
 * Contains - Banners | Video Details | Playlist etc
 */
const ContentStack = ({ children }: React.PropsWithChildren) => {
  return (
    <VStack gridArea="contentStack" spacing={{ base: 200, lg: 300 }} align="stretch" w="100%">
      {children}
    </VStack>
  );
};

/**
 * Comments are persistent on the clip page unless disabled
 * Can feature in different grid areas based on side module visibility
 */
const Comments = ({
  children,
  globalNavigationHidden,
}: React.PropsWithChildren<{ globalNavigationHidden?: boolean }>) => {
  const { isSideModuleOpen, isSideBySideActive, isShowcaseLayout } = useLayout();
  const [space200, space300] = useToken('space', ['space.200', 'space.300']);

  const sideModuleTop = getAsideTopStyles(space200, space300, globalNavigationHidden);
  const heightStyles = getAsideHeightStyles(
    space200,
    space300,
    globalNavigationHidden,
    isShowcaseLayout,
  );
  const maxHeightStyles =
    isSideBySideActive && !isSideModuleOpen
      ? rem(1200) // Side-by-side layout with comments as the aside
      : rem(640); // Comments below the player or below video details when side-by-side is active

  const isCommentsSideBySide = isSideBySideActive && !isSideModuleOpen;

  return (
    <ModuleWrapper
      gridArea="comments"
      minH={rem(420)}
      maxH={maxHeightStyles}
      h={isSideBySideActive && !isSideModuleOpen ? heightStyles : undefined}
      w="100%"
      position={isCommentsSideBySide ? 'sticky' : 'relative'}
      top={isCommentsSideBySide ? sideModuleTop : 'auto'}
    >
      {children}
    </ModuleWrapper>
  );
};

const Playlist = ({ children }: React.PropsWithChildren) => {
  return (
    <ModuleWrapper gridArea="playlist" h="max-content">
      {children}
    </ModuleWrapper>
  );
};

const ShowcasePlaylist = ({ children }: React.PropsWithChildren) => {
  const { isSideBySideActive } = useLayout();

  return (
    <Box
      as={motion.div}
      layout={!isSideBySideActive ? 'position' : false} // do not want playlist to animate width to/from side by side
      backgroundColor="background" // aids with animation, matches page bg
      gridArea="playlist"
      h="max-content"
    >
      {children}
    </Box>
  );
};

/**
 * Side modules on the Clip page
 * Contains - Vimeo AI | Transcript | Chapters etc
 * Only visible on desktop
 */
const SideModule = ({
  children,
  globalNavigationHidden,
}: React.PropsWithChildren<{ globalNavigationHidden?: boolean }>) => {
  const { isShowcaseLayout } = useLayout();
  const [space200, space300] = useToken('space', ['space.200', 'space.300']);

  const sideModuleTop = getAsideTopStyles(space200, space300, globalNavigationHidden);
  const heightStyles = getAsideHeightStyles(
    space200,
    space300,
    globalNavigationHidden,
    isShowcaseLayout,
  );

  return (
    <ModuleWrapper
      gridArea="sideModule"
      w="100%"
      minH={rem(420)}
      maxH={rem(1200)}
      h={heightStyles}
      position="sticky"
      top={sideModuleTop}
    >
      {children}
    </ModuleWrapper>
  );
};

/**
 * Mobile drawer for the Clip page
 * Contains - Vimeo AI | Transcript | Chapters | Comments | Download etc
 * On mobile these drawers are no longer rendered on the page but are rendered within a Drawer modal
 */
const MobileDrawer = ({
  children,
  playerContainerRef,
  containerStyle,
  contentStyle,
}: React.PropsWithChildren<{
  clipRequestId?: string;
  playerContainerRef: React.RefObject<HTMLDivElement | null>;
  containerStyle?: CSSProperties;
  contentStyle?: CSSProperties;
}>) => {
  const { isMobileDrawerOpen, setActiveMobileDrawer } = useLayout();
  const [space200] = useToken('space', ['space.200']);

  const playerBottom = playerContainerRef.current?.getBoundingClientRect()?.bottom || 0;
  const isPlayerInView = useInView(playerContainerRef, { margin: `-${NAV_HEIGHT}px 0px 0px 0px` });
  const drawerTop = Math.max(playerBottom, NAV_HEIGHT);

  const handleClick = useCallback(() => {
    if (isMobileDrawerOpen) {
      setActiveMobileDrawer?.('');
    }
  }, [isMobileDrawerOpen, setActiveMobileDrawer]);

  useEffect(() => {
    const element = document.getElementById('global-nav');
    if (element) {
      element.addEventListener('click', handleClick);
    }

    return () => {
      if (element) {
        element.removeEventListener('click', handleClick);
      }
    };
  }, [handleClick]);

  return (
    <Drawer
      isOpen={isMobileDrawerOpen}
      onClose={() => setActiveMobileDrawer?.('')}
      placement="bottom"
      blockScrollOnMount
      closeOnOverlayClick={false}
      trapFocus={false}
    >
      <DrawerContent
        style={contentStyle}
        containerProps={{
          style: containerStyle,
          top: isPlayerInView
            ? `calc(min(calc(${rem(drawerTop)} + ${space200}), calc(100vh - 350px)))`
            : rem(drawerTop),
          bottom: 0,
        }}
        sx={{
          '&[data-placement="bottom"]': {
            maxW: '100vw',
            mb: 0,
            top: 'inherit',
          },
          '@media screen and (max-width: 767px) and (orientation: landscape)': {
            '&[data-placement="bottom"]': {
              top: rem(NAV_HEIGHT),
            },
          },
        }}
      >
        <ModuleWrapper h="100%">{children}</ModuleWrapper>
      </DrawerContent>
    </Drawer>
  );
};

ClipLayout.Media = Media;
ClipLayout.Comments = Comments;
ClipLayout.Playlist = Playlist;
ClipLayout.ShowcasePlaylist = ShowcasePlaylist;
ClipLayout.SideModule = SideModule;
ClipLayout.ContentStack = ContentStack;
ClipLayout.MobileDrawer = MobileDrawer;
